import type { PixelConfig } from '@/config';
import type { QueueStoragePersistence } from '@/config/shared/persistence';

import { IndexedDBQueueStorage } from './IndexedDBQueueStorage';
import { LocalStorageQueueStorage } from './LocalStorageQueueStorage';
import { MemoryQueueStorage } from './MemoryQueueStorage';
import type { IQueueStorage } from './types';

export type QueueStorageFactoryOptions = {
  readonly preferred?: QueueStoragePersistence;
  readonly namespace: string;
  readonly prefix?: string;
  readonly pixelId?: string;
};

function buildStorageKey(options: QueueStorageFactoryOptions): string {
  const prefix = options.prefix ?? '__gmk';
  const namespace = options.namespace || 'pixel';
  const pixelId = options.pixelId || 'default';
  return `${prefix}:${namespace}:${pixelId}:transport-queue`;
}

function buildDbName(options: QueueStorageFactoryOptions): string {
  const namespace = options.namespace || 'pixel';
  const pixelId = options.pixelId || 'default';
  return `${namespace}_${pixelId}_transport_queue`;
}

function createByType(
  type: QueueStoragePersistence,
  options: QueueStorageFactoryOptions
): IQueueStorage {
  const key = buildStorageKey(options);

  switch (type) {
    case 'localStorage':
      return new LocalStorageQueueStorage(key);
    case 'indexedDB':
      return new IndexedDBQueueStorage(buildDbName(options));
    case 'memory':
    default:
      return new MemoryQueueStorage();
  }
}

function resolveFallbackChain(
  preferred?: QueueStoragePersistence
): QueueStoragePersistence[] {
  const order: QueueStoragePersistence[] = [
    'indexedDB',
    'localStorage',
    'memory',
  ];

  if (!preferred || preferred === 'memory') {
    return order;
  }

  const rest = order.filter(item => item !== preferred);
  return [preferred, ...rest];
}

export function createQueueStorage(
  options: QueueStorageFactoryOptions
): IQueueStorage {
  if (typeof window === 'undefined') {
    return new MemoryQueueStorage();
  }

  const chain = resolveFallbackChain(options.preferred);

  for (const type of chain) {
    const storage = createByType(type, options);
    if (storage.isAvailable()) {
      return storage;
    }
  }

  return new MemoryQueueStorage();
}

export function createQueueStorageFromConfig(
  config: PixelConfig
): IQueueStorage {
  return createQueueStorage({
    preferred: config.storage.queue?.persistence,
    namespace: config.storage.namespace,
    prefix: config.storage.prefix,
    pixelId: config.identity.workspaceId || 'default',
  });
}

export function resolveQueueStorageAdapterName(
  preferred?: QueueStoragePersistence
): string {
  const chain = resolveFallbackChain(preferred);

  for (const type of chain) {
    const storage = createByType(type, {
      preferred: type,
      namespace: 'probe',
      pixelId: 'probe',
    });
    if (storage.isAvailable()) {
      return storage.getAdapterName();
    }
  }

  return 'MemoryQueueStorage';
}
